import { useCallback } from 'react';

import { usePaymentContext } from 'features/payment';

import { PaymentMethodSelectorProps } from 'experiments/checkout-v2/treatment/features/payment/components/payment-method-selector';
import { usePrepaidsPaymentMethods as usePrepaidMethods } from 'hooks/prepaid/use-prepaids-payment-methods';

import { SelectionType } from '../../payment/components/payment-method-selector/types';

import { useCheckoutLoadingState } from './use-checkout-loading-state';
import { useCheckoutState } from './use-checkout-state';

export function usePaymentMethodSelectorProps() {
  const {
    paymentMethods,
    checkoutPaymentMethodId,
    loading: loadingPaymentMethods,
    savingPaymentMethods,
    noPaymentMethodsAvailable,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
    setDefaultReloadPaymentMethodId,
    setIsAddCreditCardModalVisible,
    setIsAddReloadPrepaidMethodModalVisible,
    setIsAddUpdateCardForReloadModalVisible,
    setAccountToDelete: setAccountToUpdate,
  } = usePaymentContext();

  const { isSubmittingOrder } = useCheckoutLoadingState();

  const { showReloadPaymentMethods, reloadAmount } = useCheckoutState();
  const { reloadPaymentMethods, selectedReloadPaymentMethod } = usePrepaidMethods();
  // Wondering if we should pass this as a prop instead of using the context
  const { serviceMode } = useCheckoutState();

  const handleSelectPaymentMethod = useCallback(
    (type: SelectionType, id?: string | undefined) => {
      switch (type) {
        case 'add':
          setAccountToUpdate('');
          setTimeout(() => {
            if (id?.includes('reload-')) {
              setIsAddUpdateCardForReloadModalVisible(true);
            } else {
              setIsAddCreditCardModalVisible(true);
            }
          }, 100);
          break;

        case 'update':
          if (id?.includes('reload-')) {
            const sanitizedId = (id || '').replace('reload-', '');
            setAccountToUpdate(sanitizedId);
          } else {
            setAccountToUpdate(id || '');
            setTimeout(() => {
              setIsAddCreditCardModalVisible(true);
            }, 100);
          }
          break;

        case 'prepaid':
          setTimeout(() => {
            setIsAddReloadPrepaidMethodModalVisible(true);
          }, 100);
          break;

        case 'selection':
          if (showReloadPaymentMethods && id?.includes('prepaid-')) {
            const sanitizedId = (id || '').replace('prepaid-', '');
            setPrepaidReloadPaymentMethodId(sanitizedId);
            setDefaultReloadPaymentMethodId(sanitizedId);
          } else {
            setDefaultPaymentMethodId(id ?? '');
            if (!id) {
              setCheckoutPaymentMethodId('');
              return;
            }
            setCheckoutPaymentMethodId(id);
          }
          break;
      }
    },
    [
      setAccountToUpdate,
      showReloadPaymentMethods,
      setIsAddUpdateCardForReloadModalVisible,
      setIsAddCreditCardModalVisible,
      setIsAddReloadPrepaidMethodModalVisible,
      setPrepaidReloadPaymentMethodId,
      setDefaultReloadPaymentMethodId,
      setDefaultPaymentMethodId,
      setCheckoutPaymentMethodId,
    ]
  );

  return {
    isLoading: !isSubmittingOrder && (loadingPaymentMethods || savingPaymentMethods),
    methods: paymentMethods,
    selectedMethodId: checkoutPaymentMethodId,
    serviceMode,
    noPaymentMethodsAvailable: noPaymentMethodsAvailable || paymentMethods.length === 0,
    prepaid: {
      shouldReloadPrepaidMethod: showReloadPaymentMethods,
      reloadAmount,
      allowedMethodsForReload: reloadPaymentMethods,
      selectedMethodId:
        (selectedReloadPaymentMethod?.accountIdentifier ||
          selectedReloadPaymentMethod?.fdAccountId) ??
        undefined,
    },
    onSelect: handleSelectPaymentMethod,
  } as PaymentMethodSelectorProps;
}
